import { useMemo, useState, type DragEvent } from 'react'
import { useDeleteMedia, useMyMedias } from '@/hooks/queries'
import { backgroundImage } from '@/lib/format'
import { cn } from '@/lib/cn'
import type { Media } from '@/models/media'
import type { CoverSpec } from '@/lib/cover-spec'
import { UploadMedia } from './UploadMedia'
import { MediaEditor } from './MediaEditor'
import { SkeletonLine } from './ui/Skeleton'

interface MyMediaProps {
  /** Present only when rendered inside an overlay. */
  onClose?: () => void
  /** When true the picker shows Cancel / Remove / Select instead of Close. */
  selecting?: boolean
  onSelect?: (media: Media | null) => void
  /** Passed on to the upload screen to crop new images to this shape. */
  coverSpec?: CoverSpec
  /**
   * The upload screen, when the parent owns the control that opens it (the
   * "Upload image" button in the /my/media header). Uncontrolled otherwise.
   */
  uploading?: boolean
  onUploadingChange?: (uploading: boolean) => void
}

const mediaName = (media: Media) => media.name ?? media.tags?.[0] ?? `#${media.id}`

const hasFiles = (event: DragEvent) => Array.from(event.dataTransfer.types).includes('Files')

/**
 * Port of components/MyMedia.vue — the /my/media library, and also the media
 * picker for every cover field.
 *
 * On the page, the library is a grid with the selected image open in a panel
 * beside it, so picking another image swaps the panel instead of scrolling the
 * page to a form under the grid. Image files dropped anywhere on the library
 * go straight to the upload screen.
 */
export function MyMedia({
  onClose,
  selecting = false,
  onSelect,
  coverSpec,
  uploading: controlledUploading,
  onUploadingChange,
}: MyMediaProps) {
  const { data: medias, isPending } = useMyMedias()
  const [ownUploading, setOwnUploading] = useState(false)
  const uploading = controlledUploading ?? ownUploading
  const setUploading = (value: boolean) => {
    setOwnUploading(value)
    onUploadingChange?.(value)
  }
  const [droppedFile, setDroppedFile] = useState<File | undefined>(undefined)
  const [dragging, setDragging] = useState(false)
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [activeTag, setActiveTag] = useState<string | null>(null)
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const deleteMedia = useDeleteMedia()

  const isPage = !onClose

  const select = (id: number | null) => {
    setSelectedId(id)
    setConfirmingDelete(false)
  }

  const onDelete = (media: Media) => {
    if (media.id === null) return
    deleteMedia.mutate(media.id, {
      onSuccess: () => select(null),
      onSettled: () => setConfirmingDelete(false),
    })
  }

  const openUpload = (file?: File) => {
    setDroppedFile(file)
    setUploading(true)
  }

  // The original hand-rolled a dedupe loop with a `seen` map; a Set does it.
  const commonTags = useMemo(
    () => [...new Set((medias ?? []).flatMap((media) => media.tags ?? []))],
    [medias],
  )

  // The tags were rendered as a row of labels that did nothing. They are the
  // only structure a library of cover art has, so they filter it.
  const shown = useMemo(
    () => (activeTag ? (medias ?? []).filter((media) => media.tags?.includes(activeTag)) : medias),
    [medias, activeTag],
  )

  const selected = medias?.find((media) => media.id === selectedId) ?? null

  if (uploading) {
    return (
      <UploadMedia
        coverSpec={coverSpec}
        initialFile={droppedFile}
        onCancel={() => {
          setDroppedFile(undefined)
          setUploading(false)
        }}
        onUploaded={(media) => {
          setDroppedFile(undefined)
          setUploading(false)
          select(media.id)
        }}
      />
    )
  }

  const dropHandlers = {
    onDragEnter: (event: DragEvent<HTMLDivElement>) => {
      if (hasFiles(event)) setDragging(true)
    },
    onDragOver: (event: DragEvent<HTMLDivElement>) => {
      if (!hasFiles(event)) return
      event.preventDefault()
      event.dataTransfer.dropEffect = 'copy'
    },
    onDragLeave: (event: DragEvent<HTMLDivElement>) => {
      // Leaving for a child still counts as inside.
      if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDragging(false)
    },
    onDrop: (event: DragEvent<HTMLDivElement>) => {
      if (!hasFiles(event)) return
      event.preventDefault()
      setDragging(false)
      openUpload(event.dataTransfer.files[0])
    },
  }

  const library = (
    <div className="my-media-library">
      {commonTags.length > 0 ? (
        <div className="tags" role="group" aria-label="Filter by tag">
          <button
            type="button"
            className={cn('tag', activeTag === null && 'active')}
            aria-pressed={activeTag === null}
            onClick={() => setActiveTag(null)}
          >
            All
          </button>
          {commonTags.map((tag) => (
            <button
              type="button"
              className={cn('tag', activeTag === tag && 'active')}
              key={tag}
              aria-pressed={activeTag === tag}
              onClick={() => setActiveTag(activeTag === tag ? null : tag)}
            >
              {tag}
            </button>
          ))}
        </div>
      ) : null}

      <div className="media-selection">
        {isPending
          ? Array.from({ length: 4 }, (_, index) => (
              <div className="media-select is-skeleton" key={index} aria-hidden="true">
                <div className="media-select-image skeleton" />
                <div className="media-select-name">
                  <SkeletonLine width="4.5em" />
                </div>
              </div>
            ))
          : shown?.map((media) => (
              <button
                type="button"
                className="media-select"
                key={media.id}
                aria-pressed={media.id === selectedId}
                onClick={() => select(media.id === selectedId && isPage ? null : media.id)}
              >
                <div
                  className={cn('media-select-image', media.id === selectedId && 'selected')}
                  style={backgroundImage(media.src)}
                />
                <div className="media-select-name">{mediaName(media)}</div>
              </button>
            ))}

        {/* With an empty library this tile sat above an empty state that
            explained it — two things saying "start here". The empty state
            carries the action instead, and the tile returns once there is a
            library for it to sit at the end of. */}
        {isPending || (medias?.length ?? 0) > 0 ? (
          <button type="button" className="media-select" onClick={() => openUpload()}>
            <div className="media-select-image upload">
              <svg viewBox="0 0 24 24" aria-hidden="true" className="media-select-glyph">
                <path d="M12 16V5M7.5 9.5 12 5l4.5 4.5M5 19h14" />
              </svg>
              <div className="media-select-text">{isPage ? 'Upload or drop' : 'Upload'}</div>
            </div>
          </button>
        ) : null}
      </div>

      {!isPending && medias?.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-title">No media yet</div>
          <div className="empty-state-body">
            Upload cover art once and reuse it across every giveaway you run.
            {isPage ? ' You can also drop an image file here.' : ''}
          </div>
          <button type="button" className="account-primary btn-sheen" onClick={() => openUpload()}>
            <svg viewBox="0 0 16 16" aria-hidden="true" className="account-primary-glyph">
              <path d="M8 3.2v9.6M3.2 8h9.6" />
            </svg>
            Upload an image
          </button>
        </div>
      ) : null}

      {!isPending && medias && medias.length > 0 && shown?.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-title">Nothing tagged “{activeTag}”</div>
          <div className="empty-state-body">
            <button type="button" className="link-button" onClick={() => setActiveTag(null)}>
              Show all media
            </button>
          </div>
        </div>
      ) : null}
    </div>
  )

  if (isPage) {
    return (
      <div
        className={cn('my-media is-page', selected && 'has-selection', dragging && 'is-dragging')}
        {...dropHandlers}
      >
        {library}

        {selected ? (
          <MediaEditor
            key={selected.id}
            media={selected}
            onClose={() => select(null)}
            onDeleted={() => select(null)}
          />
        ) : null}

        {dragging ? (
          <div className="my-media-drop" aria-hidden="true">
            <div className="my-media-drop-label">Drop to upload</div>
          </div>
        ) : null}
      </div>
    )
  }

  return (
    <div className="my-media" {...dropHandlers}>
      {library}

      {/* In a picker, selecting is for choosing, so only delete is offered
          alongside it. */}
      {selected ? (
        <div className="media-actions" role="group" aria-label="Selected image">
          <span className="media-actions-name">{mediaName(selected)}</span>
          {confirmingDelete ? (
            <>
              <span className="media-actions-prompt">Delete this image?</span>
              <button
                type="button"
                className="media-action"
                onClick={() => setConfirmingDelete(false)}
                disabled={deleteMedia.isPending}
              >
                Keep
              </button>
              <button
                type="button"
                className="media-action danger"
                onClick={() => onDelete(selected)}
                disabled={deleteMedia.isPending}
              >
                {deleteMedia.isPending ? 'Deleting…' : 'Delete'}
              </button>
            </>
          ) : (
            <button
              type="button"
              className="media-action danger"
              onClick={() => setConfirmingDelete(true)}
            >
              Delete
            </button>
          )}
        </div>
      ) : null}

      <div className="footer no-margin">
        <div className={cn('cta-row', !selecting && 'center')}>
          <button type="button" className="cta" onClick={onClose}>
            {selecting ? 'Cancel' : 'Close'}
          </button>

          {selecting ? (
            <button type="button" className="cta remove" onClick={() => onSelect?.(null)}>
              Remove
            </button>
          ) : null}

          {selecting ? (
            <button
              type="button"
              className="cta next"
              disabled={!selected}
              onClick={() => selected && onSelect?.(selected)}
            >
              Select
            </button>
          ) : null}
        </div>
      </div>
    </div>
  )
}
